import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Modal,
  TextInput,
  Text,
  Group,
  Box,
  Avatar,
  ScrollArea,
  Stack,
  UnstyledButton,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { VehicleMake } from "@/utils/api";
import { useAppEssentialsContext } from "@/contexts";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface VehicleMakePickerModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Callback to close the modal */
  onClose: () => void;

  /** Currently selected vehicle make ID */
  value: number | null;

  /** Callback when a make is selected */
  onSelect: (makeId: number | null, make?: VehicleMake) => void;

  /** Custom filter function to filter vehicle makes */
  makeFilter?: (make: VehicleMake) => boolean;

  /** Whether to show logo/avatar in options */
  showLogo?: boolean;
}

// ============================================
// Constants
// ============================================

const ICON_SIZES = {
  search: 20,
  option: 24,
  optionIcon: 14,
  optionFlat: 20,
  selectedCheck: 18,
};

const FUSE_OPTIONS: IFuseOptions<VehicleMake> = {
  keys: [
    { name: "makeName", weight: 0.8 },
    { name: "code", weight: 0.2 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 1,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

export const VehicleMakePickerModal: React.FC<VehicleMakePickerModalProps> = ({
  opened,
  onClose,
  value,
  onSelect,
  makeFilter,
  showLogo = true,
}) => {
  const { t } = useTranslation(["cars", "common"]);
  const { appEssentials } = useAppEssentialsContext();

  const vehicleMakes = appEssentials?.vehicleMakes || [];

  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset search when modal closes
  useEffect(() => {
    if (!opened) {
      setSearchValue("");
    }
  }, [opened]);

  // Apply custom filter, then sort alphabetically
  const sortedMakes = useMemo(() => {
    let filtered = vehicleMakes;
    if (makeFilter) {
      filtered = filtered.filter(makeFilter);
    }
    return [...filtered].sort((a, b) =>
      (a.makeName || "").localeCompare(b.makeName || "")
    );
  }, [vehicleMakes, makeFilter]);

  // Build Fuse index
  const fuse = useMemo(() => new Fuse(sortedMakes, FUSE_OPTIONS), [sortedMakes]);

  // Filter makes based on search
  const filteredMakes = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return sortedMakes;
    return fuse.search(search).map((result) => result.item);
  }, [sortedMakes, searchValue, fuse]);

  // Handle make selection
  const handleSelect = useCallback(
    (make: VehicleMake) => {
      if (make.id !== undefined) {
        onSelect(make.id, make);
        onClose();
      }
    },
    [onSelect, onClose]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
    },
    []
  );

  // Handle clearing search
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    searchInputRef.current?.focus();
  }, []);

  const hasResults = filteredMakes.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={t("cars:selectors.vehicleMake.modalTitle", {
        defaultValue: t("cars:selectors.vehicleMake.label"),
      })}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100dvh - 60px)",
          padding: 0,
          overflow: "hidden",
        },
        header: {
          padding: "12px 16px",
        },
        content: {
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        px="md"
        py="sm"
        style={{
          flexShrink: 0,
          borderBottom: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <TextInput
          ref={searchInputRef}
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t("cars:selectors.vehicleMake.searchPlaceholder", {
            defaultValue: t("cars:selectors.vehicleMake.placeholder"),
          })}
          leftSection={<AppIcon iconName="Search" size={ICON_SIZES.search} />}
          rightSection={
            searchValue ? (
              <CloseButton
                size="sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label={t("common:form.clearSearch")}
              />
            ) : undefined
          }
          size="md"
          autoComplete="off"
        />
      </Box>

      {/* Scrollable Options List */}
      <ScrollArea
        style={{ flex: 1, minHeight: 0 }}
        type="scroll"
        offsetScrollbars
      >
        <Stack gap={0} pb="md">
          {/* No results message */}
          {!hasResults && searchValue && (
            <Box px="md" py="xl">
              <Text c="dimmed" ta="center" size="sm">
                {t("common:form.noOptions")}
              </Text>
            </Box>
          )}

          {/* Vehicle make items */}
          {filteredMakes.map((make) => {
            const isSelected = value === make.id;

            return (
              <UnstyledButton
                key={make.id}
                onClick={() => handleSelect(make)}
                w="100%"
                px="md"
                py="sm"
                style={(theme) => ({
                  backgroundColor: isSelected
                    ? `var(--mantine-color-${theme.primaryColor}-0)`
                    : undefined,
                  borderBottom: "1px solid var(--mantine-color-gray-1)",
                  "&:active": {
                    backgroundColor: "var(--mantine-color-gray-1)",
                  },
                })}
              >
                <Group gap="sm" wrap="nowrap">
                  {showLogo ? (
                    <Avatar size={ICON_SIZES.option} radius="sm" color="gray">
                      <AppIcon iconName="Car" size={ICON_SIZES.optionIcon} />
                    </Avatar>
                  ) : (
                    <AppIcon iconName="Car" size={ICON_SIZES.optionFlat} />
                  )}
                  <Text size="md" fw={isSelected ? 600 : 500}>
                    {make.makeName}
                  </Text>

                  {isSelected && (
                    <Box ml="auto">
                      <AppIcon
                        iconName="Check"
                        size={ICON_SIZES.selectedCheck}
                        color="var(--mantine-color-blue-6)"
                      />
                    </Box>
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};